import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../services/firebase/config';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, MessageSquare, Target, BookOpen, AlertCircle, Calendar, PieChart, Filter, Activity, BarChart3, Briefcase, Clock, Zap, Lightbulb, FileText } from 'lucide-react';

interface AssessmentData {
  assessmentId: string;
  userId: string;
  assessmentDate: any;
  rawChatTranscript: string;
  extractedSkills: Array<{ 
    skill: string;
    domain: string;
    proficiency: number;
    confidence: number;
  }>;
  identifiedKnowledgeGaps: string[];
  parsedLearningGoals: Array<{ 
    goalType: string;
    description: string;
  }>;
  userEmail?: string;
  userName?: string;
  userSubscriptionStatus?: string;
  userSubscriptionTier?: string;
  userCreatedAt?: any;
  userLastLoginAt?: any;
  currentRole?: string;
  industry?: string;
  aiExperienceLevel?: number;
  timeCommitmentWeeklyHours?: number;
  bestMatchScore?: number;
  recommendedCourseTitles?: string[];
}

interface StrategyReportRecommendation {
  title: string; // "Title"
  targetAudience: string; // "Target Audience"
  why: string; // "The Why"
  keyLearningOutcomes: string[]; // "Key Learning Outcomes"
}

interface StrategyReport {
  id: string;
  generatedAt: any;
  gapCount: number;
  analysis: string;
  recommendations: StrategyReportRecommendation[];
}

type TabType = 'individual' | 'global';
type FilterStatus = 'all' | 'active' | 'inactive';

const AssessmentReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('individual');
  const [assessments, setAssessments] = useState<AssessmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentData | null>(null);

  // Filters for Global Insights
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [tierFilter, setTierFilter] = useState<string>('all');

  // Strategy Report State
  const [strategyReport, setStrategyReport] = useState<StrategyReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(true);

  useEffect(() => {
    fetchAssessments();
    fetchLatestStrategyReport();
  }, []);

  const fetchAssessments = async () => {
    try {
      const q = query(
        collection(db, 'assessments'),
        orderBy('assessmentDate', 'desc'),
        limit(200)
      );
      
      const querySnapshot = await getDocs(q);
      
      const userPromises = querySnapshot.docs.map(async (docSnapshot) => {
        const data = docSnapshot.data() as AssessmentData;
        try {
          const userDocRef = doc(db, 'users', data.userId);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            data.userEmail = userData.email;
            data.userName = userData.displayName || userData.firstName ? `${userData.firstName} ${userData.lastName || ''}` : 'Unknown';
            data.userSubscriptionStatus = userData.subscriptionStatus || 'unknown';
            data.userSubscriptionTier = userData.subscriptionTier || 'free';
            data.userCreatedAt = userData.createdAt;
            data.userLastLoginAt = userData.lastLoginAt;
            data.currentRole = userData.currentRole;
            data.industry = userData.industry;
            data.aiExperienceLevel = userData.aiExperienceLevel;
            data.timeCommitmentWeeklyHours = userData.timeCommitmentWeeklyHours;

            if (userData.currentLearningPlanId) {
              const planDocRef = doc(db, 'learningPlans', userData.currentLearningPlanId);
              const planSnap = await getDoc(planDocRef);
              if (planSnap.exists()) {
                const planData = planSnap.data();
                if (planData.recommendedCourses && planData.recommendedCourses.length > 0) {
                  const scores = planData.recommendedCourses
                    .map((c: any) => c.matchScore)
                    .filter((s: any) => typeof s === 'number');
                  
                  data.bestMatchScore = scores.length > 0 ? Math.max(...scores) : undefined;
                  data.recommendedCourseTitles = planData.recommendedCourses.map((c: any) => c.courseTitle);
                }
              }
            }
          } else {
             data.userSubscriptionStatus = 'unknown';
             data.userSubscriptionTier = 'unknown';
          }
        } catch (err) {
          console.warn(`Could not fetch details for assessment ${data.assessmentId}`, err);
        }
        return data;
      });

      const results = await Promise.all(userPromises);
      setAssessments(results);
    } catch (error) {
      console.error("Error fetching assessments:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestStrategyReport = async () => {
    try {
      const q = query(
        collection(db, 'content_strategy_reports'),
        orderBy('generatedAt', 'desc'),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const doc = snap.docs[0];
        // Map raw data to our interface, handling potential key variations from LLM
        const rawData = doc.data();
        // Normalize recommendations keys if needed
        const recommendations = (rawData.recommendations || []).map((r: any) => ({
          title: r.title || r.Title || "Untitled Concept",
          targetAudience: r.targetAudience || r["Target Audience"] || "General Audience",
          why: r.why || r['The "Why"'] || r["The Why"] || "To address identified gaps.",
          keyLearningOutcomes: r.keyLearningOutcomes || r["Key Learning Outcomes"] || []
        }));

        setStrategyReport({
          id: doc.id,
          generatedAt: rawData.generatedAt,
          gapCount: rawData.gapCount,
          analysis: rawData.analysis,
          recommendations
        });
      }
    } catch (err) {
      console.error("Error fetching strategy report:", err);
    } finally {
      setLoadingReport(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // --- Aggregation Logic for Global Insights ---
  const filteredAssessments = useMemo(() => {
    return assessments.filter(a => {
      if (statusFilter !== 'all') {
        const isActive = a.userSubscriptionStatus === 'active' || a.userSubscriptionStatus === 'trialing';
        if (statusFilter === 'active' && !isActive) return false;
        if (statusFilter === 'inactive' && isActive) return false;
      }
      if (tierFilter !== 'all' && a.userSubscriptionTier !== tierFilter) return false;
      return true;
    });
  }, [assessments, statusFilter, tierFilter]);

  const insights = useMemo(() => {
    const skillCounts: Record<string, number> = {};
    const gapCounts: Record<string, number> = {};
    const goalTypeCounts: Record<string, number> = {};
    let totalProficiency = 0;
    let skillCount = 0;
    
    // Low Match Tracking (Client-side view)
    const lowMatchClusters: Array<{ skills: string[], gaps: string[], bestScore: number }> = [];

    filteredAssessments.forEach(a => {
      // Skills
      a.extractedSkills?.forEach(s => {
        skillCounts[s.skill] = (skillCounts[s.skill] || 0) + 1;
        totalProficiency += s.proficiency;
        skillCount++;
      });
      // Gaps
      a.identifiedKnowledgeGaps?.forEach(g => {
        const gap = g.trim(); 
        gapCounts[gap] = (gapCounts[gap] || 0) + 1;
      });
      // Goals
      a.parsedLearningGoals?.forEach(g => {
        goalTypeCounts[g.goalType] = (goalTypeCounts[g.goalType] || 0) + 1;
      });

      if (a.bestMatchScore !== undefined && a.bestMatchScore < 70) {
        lowMatchClusters.push({
          skills: a.extractedSkills?.map(s => s.skill) || [],
          gaps: a.identifiedKnowledgeGaps || [],
          bestScore: a.bestMatchScore
        });
      }
    });

    const topSkills = Object.entries(skillCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    const topGaps = Object.entries(gapCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);
      
    // Identify top gaps as "Course Opportunities" (Client side quick view)
    const courseOpportunities = topGaps.filter(([, count]) => count > 1);

    const topGoals = Object.entries(goalTypeCounts)
      .sort(([, a], [, b]) => b - a);

    const avgProficiency = skillCount > 0 ? (totalProficiency / skillCount).toFixed(1) : '0';

    return { 
      topSkills, 
      topGaps, 
      topGoals, 
      avgProficiency, 
      count: filteredAssessments.length, 
      courseOpportunities,
      lowMatchClusters 
    };
  }, [filteredAssessments]);

  const availableTiers = useMemo(() => {
    const tiers = new Set(assessments.map(a => a.userSubscriptionTier).filter(Boolean));
    return Array.from(tiers);
  }, [assessments]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Navigation */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard/admin/analytics')}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Assessment Review</h1>
              <p className="text-sm text-gray-500 mt-1">Review individual results or analyze global trends</p>
            </div>
          </div>
          
          {/* Tab Switcher */}
          <div className="bg-white p-1 rounded-lg border border-gray-200 flex shadow-sm">
            <button
              onClick={() => setActiveTab('individual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${ 
                activeTab === 'individual' 
                  ? 'bg-blue-50 text-blue-700 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Individual Reviews
            </button>
            <button
              onClick={() => setActiveTab('global')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${ 
                activeTab === 'global' 
                  ? 'bg-blue-50 text-blue-700 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Global Insights
            </button>
          </div>
        </div>

        {activeTab === 'individual' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* List Column */}
            <div className="bg-white rounded-lg shadow overflow-hidden lg:col-span-1 h-[calc(100vh-200px)] flex flex-col">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-700">Recent Assessments</h2>
                <span className="text-xs font-medium bg-gray-200 text-gray-600 px-2 py-1 rounded-full">{assessments.length}</span>
              </div>
              <div className="overflow-y-auto flex-1 p-2 space-y-2">
                {loading ? (
                  <div className="text-center py-8 text-gray-500 flex flex-col items-center">
                    <Activity className="w-8 h-8 animate-spin mb-2 opacity-50" />
                    Loading assessments...
                  </div>
                ) : assessments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No assessments found.</div>
                ) : (
                  assessments.map((assessment) => (
                    <button
                      key={assessment.assessmentId}
                      onClick={() => setSelectedAssessment(assessment)}
                      className={`w-full text-left p-4 rounded-lg transition-all border ${ 
                        selectedAssessment?.assessmentId === assessment.assessmentId
                          ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-300 shadow-sm'
                          : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 w-full">
                          <div className={`p-2 rounded-full shrink-0 ${ 
                            assessment.userSubscriptionStatus === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <User className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">
                              {assessment.userEmail || assessment.userId}
                            </p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-500 flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatDate(assessment.assessmentDate).split(',')[0]}
                              </span>
                              {assessment.userSubscriptionTier && (
                                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100 truncate max-w-[80px]">
                                  {assessment.userSubscriptionTier.replace('_', ' ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Detail Column */}
            <div className="lg:col-span-2 h-[calc(100vh-200px)] overflow-y-auto rounded-lg shadow bg-white border border-gray-200">
              {selectedAssessment ? (
                <div className="p-6 space-y-8">
                  {/* Assessment Header */}
                  <div className="border-b border-gray-100 pb-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Assessment Details</h2>
                        <p className="text-sm text-gray-500">ID: {selectedAssessment.assessmentId}</p>
                      </div>
                      <div className="text-right">
                         <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                           <Calendar className="w-4 h-4 mr-2" />
                           {formatDate(selectedAssessment.assessmentDate)}
                         </div>
                      </div>
                    </div>

                    {/* User Summary Card */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Profile Info */}
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">User Profile</p>
                            <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-100">
                              <div className="bg-blue-100 p-2 rounded-full">
                                <User className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{selectedAssessment.userName}</p>
                                <p className="text-sm text-gray-600">{selectedAssessment.userEmail}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white p-3 rounded-lg border border-gray-100">
                              <p className="text-xs text-gray-500 mb-1">Status</p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${ 
                                selectedAssessment.userSubscriptionStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {selectedAssessment.userSubscriptionStatus || 'Unknown'}
                              </span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-100">
                              <p className="text-xs text-gray-500 mb-1">Plan</p>
                              <span className="text-sm font-medium text-gray-900 capitalize">
                                {(selectedAssessment.userSubscriptionTier || 'Free').replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Assessment Profile (Snapshot) */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Assessment Profile</p>
                          <div className="bg-white p-4 rounded-lg border border-gray-100 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Briefcase className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">Role</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">{selectedAssessment.currentRole || 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">Industry</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">{selectedAssessment.industry || 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">AI Experience</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-gray-900">{selectedAssessment.aiExperienceLevel || 0}</span>
                                <span className="text-xs text-gray-400">/ 5</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">Weekly Commitment</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">{selectedAssessment.timeCommitmentWeeklyHours || 0}h</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Extracted Skills */}
                  <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-5 h-5 text-green-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Identified Skills</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedAssessment.extractedSkills?.map((skill, idx) => (
                        <div key={idx} className="bg-white p-3 rounded-lg border border-green-200 shadow-sm flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-800">{skill.skill}</p>
                            <p className="text-xs text-gray-500 capitalize">{skill.domain}</p>
                          </div>
                          <div className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded text-xs font-medium text-green-700">
                            <span>Lvl {skill.proficiency}</span>
                          </div>
                        </div>
                      )) || <p className="text-gray-500">No skills identified.</p>}
                    </div>
                  </div>

                  {/* Learning Goals */}
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Learning Goals</h3>
                    </div>
                    <ul className="space-y-3">
                      {selectedAssessment.parsedLearningGoals?.map((goal, idx) => (
                        <li key={idx} className="bg-white p-3 rounded-lg border border-blue-200 shadow-sm">
                          <p className="font-medium text-gray-900 mb-1 capitalize">{goal.goalType}</p>
                          <p className="text-gray-600 text-sm">{goal.description}</p>
                        </li>
                      )) || <p className="text-gray-500">No goals identified.</p>}
                    </ul>
                  </div>

                  {/* Knowledge Gaps */}
                  <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Knowledge Gaps</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedAssessment.identifiedKnowledgeGaps?.map((gap, idx) => (
                        <span key={idx} className="bg-white px-3 py-1.5 rounded-full border border-amber-200 text-amber-800 text-sm font-medium shadow-sm">
                          {gap}
                        </span>
                      )) || <p className="text-gray-500">No specific gaps identified.</p>}
                    </div>
                  </div>

                  {/* Transcript */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <MessageSquare className="w-5 h-5 text-gray-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Chat Transcript</h3>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-sm font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {selectedAssessment.rawChatTranscript || 'No transcript available.'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                  <Target className="w-16 h-16 mb-4 opacity-20" />
                  <p className="text-lg">Select an assessment to view details</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-8 animate-in fade-in duration-300">
             {/* Global Insights Tab */}
             
             {/* Filters */}
             <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
               <div className="flex items-center gap-2 mb-4 text-gray-700">
                 <Filter className="w-5 h-5" />
                 <h2 className="font-semibold text-lg">Filters</h2>
               </div>
               <div className="flex flex-wrap gap-6">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">User Status</label>
                   <select 
                     value={statusFilter}
                     onChange={(e) => setStatusFilter(e.target.value as FilterStatus)}
                     className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                   >
                     <option value="all">All Users</option>
                     <option value="active">Active Subscribers</option>
                     <option value="inactive">Inactive / Free</option>
                   </select>
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Plan</label>
                   <select 
                     value={tierFilter}
                     onChange={(e) => setTierFilter(e.target.value)}
                     className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border capitalize"
                   >
                     <option value="all">All Plans</option>
                     {availableTiers.map(tier => (
                       <option key={tier} value={tier}>{String(tier).replace(/_/g, ' ')}</option>
                     ))}
                   </select>
                 </div>
               </div>
               <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500 flex justify-between">
                 <span>Analysis based on {insights.count} loaded assessments</span>
                 {insights.count < 200 ? null : <span className="text-amber-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Limited to most recent 200</span>}
               </div>
             </div>

             {/* Stat Cards */}
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Assessments Analyzed</p>
                      <h3 className="text-3xl font-bold text-gray-900 mt-1">{insights.count}</h3>
                    </div>
                    <Activity className="w-8 h-8 text-blue-100" />
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                   <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Avg. Skill Proficiency</p>
                      <h3 className="text-3xl font-bold text-gray-900 mt-1">{insights.avgProficiency} <span className="text-sm font-normal text-gray-400">/ 5</span></h3>
                    </div>
                    <BarChart3 className="w-8 h-8 text-green-100" />
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                   <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Top Goal Category</p>
                      <h3 className="text-xl font-bold text-gray-900 mt-1 truncate max-w-[150px]" title={insights.topGoals[0]?.[0] || 'N/A'}>
                        {insights.topGoals[0]?.[0] || 'N/A'}
                      </h3>
                      <p className="text-xs text-purple-600 mt-1">{insights.topGoals[0]?.[1] || 0} users</p>
                    </div>
                    <Target className="w-8 h-8 text-purple-100" />
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow border-l-4 border-amber-500">
                   <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Top Knowledge Gap</p>
                      <h3 className="text-xl font-bold text-gray-900 mt-1 truncate max-w-[150px]" title={insights.topGaps[0]?.[0] || 'N/A'}>
                        {insights.topGaps[0]?.[0] || 'N/A'}
                      </h3>
                      <p className="text-xs text-amber-600 mt-1">{insights.topGaps[0]?.[1] || 0} occurrences</p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-amber-100" />
                  </div>
                </div>
             </div>

             {/* Weekly Content Strategy Report (Automated) */}
             <div className="bg-purple-50 p-6 rounded-lg shadow border border-purple-200 lg:col-span-2">
               <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                 <div>
                   <h3 className="text-lg font-bold text-purple-900 mb-2 flex items-center gap-2">
                     <FileText className="w-5 h-5 text-purple-600" />
                     Weekly Content Strategy Report
                   </h3>
                   <p className="text-sm text-purple-700">
                     Automated analysis of content gaps and low-match assessments from the last week.
                   </p>
                 </div>
                 {strategyReport && (
                   <div className="text-xs font-medium bg-purple-100 text-purple-800 px-3 py-1 rounded-full border border-purple-200">
                     Generated: {formatDate(strategyReport.generatedAt)}
                   </div>
                 )}
               </div>

               {loadingReport ? (
                 <div className="text-center py-8 text-purple-500 flex flex-col items-center">
                   <Activity className="w-6 h-6 animate-spin mb-2" />
                   Checking for latest report...
                 </div>
               ) : strategyReport ? (
                 <div className="space-y-6">
                   <div className="prose prose-sm prose-purple max-w-none text-purple-800 bg-white/50 p-4 rounded-lg">
                     <p className="italic">{strategyReport.analysis}</p>
                   </div>
                   
                   <h4 className="font-bold text-purple-900 flex items-center gap-2 mt-4">
                     <Lightbulb className="w-4 h-4" />
                     Recommended Course Concepts
                   </h4>
                   
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     {strategyReport.recommendations.map((rec, idx) => (
                       <div key={idx} className="bg-white border border-purple-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                         <h5 className="text-lg font-bold text-purple-900 mb-2">{rec.title}</h5>
                         <div className="space-y-3 text-sm">
                           <div>
                             <span className="font-semibold text-purple-800 uppercase text-xs tracking-wide">Target Audience</span>
                             <p className="text-purple-700 mt-0.5">{rec.targetAudience}</p>
                           </div>
                           <div>
                             <span className="font-semibold text-purple-800 uppercase text-xs tracking-wide">Why this is needed</span>
                             <p className="text-purple-700 mt-0.5">{rec.why}</p>
                           </div>
                           {rec.keyLearningOutcomes && rec.keyLearningOutcomes.length > 0 && (
                             <div>
                               <span className="font-semibold text-purple-800 uppercase text-xs tracking-wide">Key Outcomes</span>
                               <ul className="list-disc list-inside text-purple-700 mt-1 ml-1 space-y-1">
                                 {rec.keyLearningOutcomes.map((t, i) => <li key={i}>{t}</li>)}
                               </ul>
                             </div>
                           )}
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               ) : (
                 <div className="text-center py-8 text-purple-500 bg-purple-100/50 rounded-lg border border-purple-100">
                   <p className="font-medium">No strategy report available yet.</p>
                   <p className="text-sm mt-1">Reports are generated automatically every Monday at 9:00 AM.</p>
                 </div>
               )}
             </div>

             {/* Charts / Lists */}
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
               
               {/* Top Skills */}
               <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                 <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                   <BookOpen className="w-5 h-5 text-gray-400" />
                   Most Reported Skills
                 </h3>
                 <div className="space-y-4">
                   {insights.topSkills.map(([skill, count], _idx) => (
                     <div key={skill}>
                       <div className="flex justify-between text-sm mb-1">
                         <span className="font-medium text-gray-700">{skill}</span>
                         <span className="text-gray-500">{count} ({Math.round((count / insights.count) * 100)}%)</span>
                       </div>
                       <div className="w-full bg-gray-100 rounded-full h-2">
                         <div 
                           className="bg-blue-500 h-2 rounded-full"
                           style={{ width: `${(count / (insights.topSkills[0]?.[1] || 1)) * 100}%` }}
                         ></div>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>

               {/* Top Gaps */}
               <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                 <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                   <AlertCircle className="w-5 h-5 text-gray-400" />
                   Common Knowledge Gaps
                 </h3>
                 <div className="space-y-4">
                   {insights.topGaps.map(([gap, count], _idx) => (
                     <div key={gap}>
                       <div className="flex justify-between text-sm mb-1">
                         <span className="font-medium text-gray-700">{gap}</span>
                         <span className="text-gray-500">{count}</span>
                       </div>
                       <div className="w-full bg-gray-100 rounded-full h-2">
                         <div 
                           className="bg-amber-500 h-2 rounded-full"
                           style={{ width: `${(count / (insights.topGaps[0]?.[1] || 1)) * 100}%` }}
                         ></div>
                       </div>
                     </div>
                   ))}
                   {insights.topGaps.length === 0 && <p className="text-gray-500 italic">No significant gaps data available.</p>}
                 </div>
               </div>

               {/* Learning Goal Categories */}
               <div className="bg-white p-6 rounded-lg shadow border border-gray-200 lg:col-span-2">
                 <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                   <PieChart className="w-5 h-5 text-gray-400" />
                   Learning Goal Categories
                 </h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                   {insights.topGoals.map(([type, count]) => (
                     <div key={type} className="bg-purple-50 p-4 rounded-lg border border-purple-100 flex items-center justify-between">
                       <span className="capitalize font-medium text-purple-900">{type}</span>
                       <span className="bg-white px-2 py-1 rounded text-purple-700 font-bold shadow-sm">{count}</span>
                     </div>
                   ))}
                 </div>
               </div>

             </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentReviewPage;
